// Client-side PDF → image rendering for the "Insert resume" editor feature.
//
// We render each page of a PDF to a PNG so the resume/document is embedded in
// the proposal *exactly as it looks* (fonts, columns, spacing preserved) rather
// than being reflowed into editable text. Pages come back as Blobs which the
// caller uploads to Azure (via the editor image-upload endpoint) and inserts as
// <img> nodes — keeping the saved HTML light instead of base64-bloating it.
//
// Uses react-pdf's bundled pdfjs so the worker config matches the rest of the
// app (see PdfViewer.jsx / FormReviewPage.jsx which set the same workerSrc).
import { pdfjs } from "react-pdf";

if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc =
    "//cdnjs.cloudflare.com/ajax/libs/pdf.js/" +
    pdfjs.version +
    "/pdf.worker.min.js";
}

/**
 * Render every page of a PDF File/Blob to PNG image blobs.
 *
 * @param {File|Blob} file            The PDF to render.
 * @param {Object}   [opts]
 * @param {number}   [opts.scale=2]   Render scale — 2 is crisp on retina at a
 *                                    reasonable file size.
 * @param {(done:number,total:number)=>void} [opts.onProgress]
 * @returns {Promise<Array<{blob: Blob, width: number, height: number, pageNumber: number}>>}
 */
export async function pdfToImageBlobs(file, { scale = 2, onProgress } = {}) {
  const data = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data }).promise;

  try {
    const pages = [];
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext("2d");

      // Paint a white background first so PDFs with transparency don't render
      // with a black backdrop.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: ctx, viewport }).promise;

      const blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Failed to encode page image"))),
          "image/png"
        )
      );

      pages.push({ blob, width: canvas.width, height: canvas.height, pageNumber });

      // Free page resources before moving on (matters for large resumes).
      if (typeof page.cleanup === "function") page.cleanup();
      onProgress?.(pageNumber, pdf.numPages);
    }
    return pages;
  } finally {
    if (typeof pdf.destroy === "function") pdf.destroy();
  }
}
